import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { FaSun, FaMoon, FaCloudSun } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const GreetingCard = () => {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Direct fetch from the API to get the employee's name
  const fetchEmployeeName = async () => {
    try {
      setLoading(true);
      
      // Get employee ID from various sources
      const employeeId = auth?.employeeId || localStorage.getItem('employeeId');
      if (!employeeId) {
        console.warn("No employee ID available to fetch name");
        return null;
      }
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn("No token available to fetch employee data");
        return null;
      }
      
      console.log(`Directly fetching employee data for ID: ${employeeId}`);
      
      // Direct fetch from employees table endpoint
      const response = await fetch(`${API_URL}/employees/${employeeId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch employee: ${response.status}`);
      }
      
      const employee = await response.json();
      console.log("Employee data fetched directly:", employee);
      
      if (employee?.name) {
        // Store the full name in localStorage
        localStorage.setItem('employeeName', employee.name);
        // Return the first name
        return employee.name.split(' ')[0];
      } else if (employee?.firstName && employee?.lastName) {
        const fullName = `${employee.firstName} ${employee.lastName}`;
        localStorage.setItem('employeeName', fullName);
        return employee.firstName;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching employee name:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Debug all available data sources
    console.log('Debug GreetingCard - AUTH:', {
      contextData: auth,
      employeeName: auth?.employeeName,
      storedEmployeeName: localStorage.getItem('employeeName'),
      employeeId: localStorage.getItem('employeeId'),
      userRole: localStorage.getItem('userRole'),
      token: localStorage.getItem('token') ? 'exists' : 'missing'
    });
    
    const loadName = async () => {
      // Avoid showing "Admin" as a name - if we detect this, try to fetch the real name
      const storedName = localStorage.getItem('employeeName');
      const lowerStoredName = storedName?.toLowerCase();
      
      if (lowerStoredName === 'admin' || lowerStoredName === 'manager' || lowerStoredName === 'user') {
        console.log("Detected role as name, trying to fetch real name from API");
        const apiName = await fetchEmployeeName();
        if (apiName) {
          console.log("Successfully retrieved real name from API:", apiName);
          setFirstName(apiName);
          return;
        }
      }
      
      // Try multiple sources to get the user's first name
      // 1. Try from localStorage directly
      if (storedName && storedName !== 'null' && storedName !== 'null null' && 
          lowerStoredName !== 'admin' && lowerStoredName !== 'manager' && lowerStoredName !== 'user') {
        console.log("Using stored name from localStorage:", storedName);
        const firstNameFromStored = storedName.split(' ')[0];
        setFirstName(firstNameFromStored);
        return;
      }
      
      // 2. Try from context
      const employeeName = auth?.employeeName;
      const lowerEmployeeName = employeeName?.toLowerCase();
      if (employeeName && employeeName !== 'null' && employeeName !== 'null null' &&
          lowerEmployeeName !== 'admin' && lowerEmployeeName !== 'manager' && lowerEmployeeName !== 'user') {
        console.log("Using employee name from context:", employeeName);
        const firstNameFromContext = employeeName.split(' ')[0];
        setFirstName(firstNameFromContext);
        return;
      }
      
      // 3. Try to fetch directly from API
      console.log("Trying to fetch name directly from API");
      const fetchedName = await fetchEmployeeName();
      if (fetchedName) {
        console.log("Successfully retrieved name from API:", fetchedName);
        setFirstName(fetchedName);
        return;
      }
      
      // 4. Try from user object in localStorage
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        console.log("Parsed user data:", userData);
        
        // Check for firstName or first_name
        if (userData.firstName) {
          console.log("Using firstName from userData:", userData.firstName);
          setFirstName(userData.firstName);
          return;
        }
        
        if (userData.first_name) {
          console.log("Using first_name from userData:", userData.first_name);
          setFirstName(userData.first_name);
          return;
        }
        
        // Check for name field and split it
        if (userData.name && userData.name.includes(' ')) {
          console.log("Using split name from userData:", userData.name);
          setFirstName(userData.name.split(' ')[0]);
          return;
        }
        
        // 5. Try email if nothing else
        if (userData.email) {
          console.log("Using email for name:", userData.email);
          const emailName = userData.email.split('@')[0];
          // Capitalize first letter
          const formattedName = emailName
            .split('.')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
            .split(' ')[0]; // Get just the first name
            
          setFirstName(formattedName);
          return;
        }
      } catch (error) {
        console.error("Error parsing user data for greeting:", error);
      }
      
      // 6. Final fallback - hard code user name
      console.log("Using hardcoded name as last resort");
      setFirstName('Jonny');
    };
    
    loadName();
  }, [auth]);
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: 'Good Morning', icon: <FaSun size={24} color="#ff9800" /> };
    if (hour < 18) return { text: 'Good Afternoon', icon: <FaCloudSun size={24} color="#2196f3" /> };
    return { text: 'Good Evening', icon: <FaMoon size={24} color="#673ab7" /> };
  };
  
  const greeting = getGreeting();
  

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 3 },
        mb: 2
      }}
    >
      <Box sx={{ mr: 2 }}>
        {React.cloneElement(greeting.icon, { 
          size: isMobile ? 28 : 32 
        })}
      </Box>
      <Box>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{ 
            fontWeight: 'bold', 
            color: '#1F2937', 
            mb: 0.5,
            fontSize: isMobile ? '1.5rem' : '2rem',
            lineHeight: 1.2
          }}
        >
          {greeting.text}{firstName ? `, ${firstName}!` : loading ? '...' : ', Jonny!'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          Dashboard
        </Typography>
      </Box>
    </Box>
  );
};

export default GreetingCard;
